<script>
import firebase from 'firebase'
import ButtonLoading from '@/components/buttons/Button.vue'

import {
  nameRule,
  emailRule,
  passwordRule
} from '../../helpers/validationRules'
import api from '../../api'
import { DEFAULT_AI_COUNTRY } from '@/utils/aiMarkets'
import { WelcomeEmail } from '../../helpers/emails'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'SignUp',
  metaInfo: {
    title: 'Sign up today!',
    titleTemplate: '%s | LinkBook.io',
    meta: [
      {
        name: 'description',
        content:
          'LinkBook.io makes life easier to find and access your favourite websites instantly with the bookmark network hub.'
      }
    ]
  },
  components: {
    ButtonLoading
  },
  data() {
    return {
      isLoading: false,
      fname: null,
      email: null,
      password: null,
      feedback: null,
      showPassword: false,
      nameRule,
      emailRule,
      passwordRule
    }
  },
  computed: {
    ...mapGetters(['authTokenRefererUrl', 'refererUrl'])
  },
  methods: {
    ...mapActions(['finalizeLogin']),
    async getEmailConfig() {
      try {
        const email = await WelcomeEmail()
        const htmlContent = await email?.html // Wait for the promise to resolve
        const config = {
          email: this.email, // Replace with the actual email
          subject: email.subject,
          html: htmlContent
        }
        return config
      } catch (error) {
        console.error('Error in getEmailConfig:', error)
        throw error
      }
    },

    async onSubmit(e) {
      e.preventDefault()
      this.isLoading = false

      if (this.email && this.password) {
        this.isLoading = true

        try {
          const userCredential = await firebase
            .auth()
            .createUserWithEmailAndPassword(this.email, this.password)

          // Send welcome email
          const emailConfig = await this.getEmailConfig() // Wait for getEmailConfig to complete
          await api.sendEmail(emailConfig) // Send welcome email

          const user = userCredential.user

          if (user) {
            // Track Meta Pixel CompleteRegistration event
            if (typeof window.fbq !== 'undefined') {
              window.fbq('track', 'CompleteRegistration', {
                content_name: 'User Signup',
                status: true
              })
            }

            const profileUpdate = {
              displayName: this.fname,
              date_created: firebase.firestore.Timestamp.fromDate(new Date()),
              settings: {
                aiPreferences: {
                  country: DEFAULT_AI_COUNTRY
                }
              }
            }

            await api.profileUpdate(profileUpdate).then(() => {
              this.finalizeLogin()
                .then(() => {
                  // Prioritize redirection: first check if there's an auth token URL
                  if (this.authTokenRefererUrl) {
                    this.$router.push(this.authTokenRefererUrl)
                  }
                  // Then check if the refererUrl exists and is not a login/signup page
                  else if (
                    this.refererUrl &&
                    this.refererUrl !== '/' &&
                    !this.refererUrl.startsWith('/login') &&
                    !this.refererUrl.startsWith('/signup') &&
                    !this.refererUrl.startsWith('/forgotpassword')
                  ) {
                    this.$router.push(this.refererUrl)
                  } else {
                    this.$router.push('/dashboard')
                  }
                })
                .catch(error => {
                  console.error('Signup finalization error:', error)
                  this.feedback = 'Signup failed. Please try again.'
                  this.isLoading = false
                })
            })
          }
        } catch (err) {
          this.feedback = err.message
        } finally {
          this.isLoading = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.login {
  max-width: 400px;
  margin-top: 60px;
  h2 {
    font-size: 2.4em;
  }
  .field {
    margin-bottom: 16px;
  }
}
</style>

<template>
  <v-card width="400" class="mx-auto mt-16">
    <v-card-title>
      <h1 class="display-1">Create account</h1>
    </v-card-title>
    <v-card-text>
      <v-form @submit="onSubmit">
        <v-text-field
          name="fname"
          label="First Name"
          v-model="fname"
          prepend-icon="mdi-account-circle"
          :rules="nameRule"
          required
        />
        <v-text-field
          name="email"
          label="Email"
          v-model="email"
          prepend-icon="mdi-email"
          :rules="emailRule"
          required
        />
        <v-text-field
          name="password"
          label="Password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          prepend-icon="mdi-lock"
          @click:append="showPassword = !showPassword"
          :rules="passwordRule"
          required
        />

        <v-divider class="my-3"></v-divider>

        <p v-if="feedback" class="my-3 red--text center">{{ feedback }}</p>
        <v-card-actions>
          <router-link to="/login">I already have an account</router-link>

          <v-spacer></v-spacer>

          <ButtonLoading color="primary" :disabled="isLoading" type="submit">
            Create account
          </ButtonLoading>
        </v-card-actions>
      </v-form>
    </v-card-text>
  </v-card>
</template>
